/* eslint-disable react/jsx-one-expression-per-line */
import { get } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import LoadingText from '../../LoadingText/LoadingText';
import SelectInput from '../../SelectInput/SelectInput';

const useStyles = makeStyles()((theme) => ({
  error: {
    color: get(theme, 'palette.destructive.main'),
  },
  formControl: {
    margin: '20px 0px',
  },
  button: {
    marginTop: '20px',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  content: {
    flexDirection: 'column',
    padding: '20px',
    minWidth: '300px',
  },
  title: {
    color: 'white',
    background: get(theme, 'palette.primary.main'),
  },
}));

const defaultStartDate = () => moment().startOf('day').format('YYYY-MM-DD');

export default function ExportModal({
  onClose,
  onExportClick,
  title = 'Export',
  open,
}) {
  const { classes } = useStyles();

  const options = useMemo(
    () => [
      { value: 'due_date', text: 'By Due Date' },
      { value: 'vendor_acceptance_date', text: 'By Acceptance Date' },
      { value: 'creation_date', text: 'By Creation Date' },
    ],
    [],
  );

  const [date, setDate] = useState({
    from: defaultStartDate(),
    to: defaultStartDate(),
  });

  const [filterDateBy, setFilterDateBy] = useState(options[1].value);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const onCloseModal = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle align="center" classes={{ root: classes.title }}>
        {title}
      </DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <FormControl
          variant="standard"
          className={classes.formControl}
          fullWidth
        >
          <SelectInput
            required
            fullWidth
            native
            value={filterDateBy || ''}
            onChange={({ target: { value } }) => setFilterDateBy(value)}
            label="Filter date by"
            autoComplete="no"
            options={options}
          />
        </FormControl>
        <FormControl
          variant="standard"
          className={classes.formControl}
          fullWidth
        >
          <TextField
            variant="standard"
            label="From"
            type="date"
            value={date.from}
            inputProps={{
              onChange: (e) => {
                e.persist();
                setDate((d) => ({ ...d, from: e.target.value }));
              },
            }}
          />
          <TextField
            variant="standard"
            label="To"
            type="date"
            value={date.to}
            inputProps={{
              onChange: (e) => {
                e.persist();
                setDate((d) => ({ ...d, to: e.target.value }));
              },
            }}
          />
        </FormControl>
        <div className={classes.error}>{error && error}</div>

        <div className={classes.buttonGroup}>
          <Button
            className={classes.button}
            onClick={onCloseModal}
            variant="outlined"
          >
            Close
          </Button>
          <Button
            className={classes.button}
            onClick={() => {
              setLoading(true);
              onExportClick({ from: date.from, to: date.to, filterDateBy })
                .catch((err) => setError(get(err, 'message')))
                .finally(() => setLoading(false));
            }}
            variant="outlined"
            color="primary"
            disabled={loading}
          >
            {loading ? <LoadingText text="Exporting" /> : 'Export Report'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

ExportModal.propTypes = {
  title: PropTypes.string,
  onExportClick: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
